import { cloneElement, ReactElement, ReactNode, useRef, useState } from "react";
import styled from "styled-components";
import {
	useOverlay,
	usePreventScroll,
	useModal,
	OverlayContainer,
	AriaOverlayProps,
} from "@react-aria/overlays";
import { useDialog } from "@react-aria/dialog";
import { FocusScope } from "@react-aria/focus";
import { LayoutProps, layout, SpaceProps, space } from "styled-system";
import css from "@styled-system/css";

import Box from "../Box/Box";
import Button from "../Button/Button";
import Typography from "../Typography/Typography";

export interface ModalProps extends AriaOverlayProps, LayoutProps, SpaceProps {
	/**
   The accessibility role for the dialog
   */
	role?: "dialog" | "alertdialog";
	/**
    Additional information to be displayed on the card
    */
	children?: ReactNode;
	/**
    The element's unique identifier
    */
	id?: string;
	/**
    Defines a string value that labels the current element
    */
	"aria-label"?: string;
	/**
    Identifies the element (or elements) that labels the current element
    */
	"aria-labelledby"?: string;
	/**
    Identifies the element (or elements) that describes the object
    */
	"aria-describedby"?: string;
	/**
    Whether to close the overlay when the user interacts outside it
    */
	isDismissable?: boolean;
	/**
    Whether the overlay should close when focus is lost or moves outside it
    */
	shouldCloseOnBlur?: boolean;
	/**
    Whether pressing the escape key to close the overlay should be disabled
    */
	isKeyboardDismissDisabled?: boolean;
	/**
    Whether the overlay is currently open.
    */
	isOpen?: boolean;
	/**
    Handler that is called when the overlay should close
    */
	onClose?: () => void;
	/**
   Allows the user to specify any custom styles.
   */
	css?: any;
	/**
   Allows the user to use a custom close button.
   */
	closeButton?: ReactElement;
}

const ModalContainer = styled(Box)`
	position: fixed;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: 999;
`;

const Card = styled(Box)<{ css?: string }>`
	box-sizing: border-box;
	position: fixed;
	top: 0;
	right: 0;
	background: white;
	z-index: 1;
	height: 100vh;
	animation: slide-in-right 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;

	&:focus {
		outline: none;
	}

	&.slide-out-right {
		animation: slide-out-right 0.3s cubic-bezier(0.55, 0.085, 0.68, 0.53) both;
	}

	@keyframes slide-in-right {
		0% {
			transform: translateZ(-1400px) translateX(1000px);
			opacity: 0;
		}
		100% {
			transform: translateZ(0) translateX(0);
			opacity: 1;
		}
	}

	@keyframes slide-out-right {
		0% {
			transform: translateX(0);
			opacity: 1;
		}
		100% {
			transform: translateX(1000px);
			opacity: 0;
		}
	}

	@media only screen and (min-width: 1024px) {
		width: 41.5%;
	}

	${layout}
	${space}
  ${(props) => props.css}
`;

const XButton = styled(Button)`
	position: absolute;
	transform: translateX(-100%);
	border-radius: 50%;
	height: auto;
	width: auto;
	left: calc(100% - 20px);
	background-color: transparent;
	box-shadow: none;
	z-index: 2;
	${css({ top: 8 })}

	&:focus {
		outline: solid;
	}

	@media only screen and (min-width: 768px) and (max-width: 1024px) {
		top: 40px;
	}

	@media only screen and (min-width: 1024px) {
		${css({
			color: "white",
			height: 36,
			width: 36,
			top: 6,
			left: "-8px",
			backgroundColor: "text-color-hover",
			boxShadow: "hover",
		})}
	}
`;

const XSymbol = styled(Typography)`
	font-weight: 100;
	font-size: 30px;
	line-height: 16px;
	@media only screen and (min-width: 1024px) {
		${css({ color: "white" })}
	}
`;

const CloseButton = ({ onClick = () => {} }) => (
	<XButton variant="tertiary" m={0} p={0} onClick={onClick}>
		<XSymbol m={0} p={0} color="primary">
			&times;
		</XSymbol>
	</XButton>
);

const FlyoutModal = ({
	onClose = () => {},
	isOpen = false,
	isDismissable = true,
	shouldCloseOnBlur = false,
	isKeyboardDismissDisabled = false,
	children,
	role = "dialog",
	id,
	"aria-label": ariaLabel,
	"aria-labelledby": ariaLabelledby,
	"aria-describedby": ariaDescribedby,
	closeButton = <CloseButton />,
	...props
}: ModalProps) => {
	const ref = useRef(null);
	const [modalClassname, setModalClassname] = useState("");

	const handleClose = () => {
		setModalClassname("slide-out-right");
		setTimeout(() => {
			onClose();
		}, 300);
	};

	const { overlayProps } = useOverlay(
		{
			isOpen,
			onClose: handleClose,
			isDismissable,
			shouldCloseOnBlur,
			isKeyboardDismissDisabled,
		},
		ref
	);

	usePreventScroll();

	const modalProps = useModal();
	const { dialogProps } = useDialog(
		{
			role,
			id,
			"aria-label": ariaLabel,
			"aria-labelledby": ariaLabelledby,
			"aria-describedby": ariaDescribedby,
		},
		ref
	);

	return (
		<OverlayContainer>
			<ModalContainer>
				<Box
					width="100vw"
					height="100vh"
					backgroundColor="primary-backdrop"
					css={`
						opacity: 0.5;
					`}
				></Box>
				<FocusScope contain restoreFocus>
					<Card
						className={modalClassname}
						ref={ref}
						width="100vw"
						{...overlayProps}
						{...dialogProps}
						{...modalProps}
						{...props}
					>
						{cloneElement(closeButton, {
							onClick: handleClose,
						})}
						{children}
					</Card>
				</FocusScope>
			</ModalContainer>
		</OverlayContainer>
	);
};

export default FlyoutModal;
